import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useTabSession from '../../hooks/useTabSession';
import { SESSION_CLOSED_PATH } from '../../services/tabSessionService';
import { getTabSessionUserKey } from '../../utils/tabSessionUtils';
import { isDesktopCallWindow } from '../../utils/liveKitCallWindow';
import { isElectronRuntime } from '../../realm/isRealmEnabled';

const TabSessionConflictModal = ({ onUseHere, onCloseOther }) => (
  <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm px-4">
    <div
      className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tab-session-conflict-title"
    >
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </div>
      <h2
        id="tab-session-conflict-title"
        className="text-lg font-semibold text-center text-gray-900 dark:text-white"
      >
        A session is already open
      </h2>
      <p className="mt-2 text-sm text-center text-gray-600 dark:text-gray-300">
        Workloop is already running in another browser tab. Choose how you want to continue.
      </p>
      <div className="mt-6 flex flex-col gap-2.5">
        <button
          type="button"
          onClick={onUseHere}
          className="w-full px-4 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          Use Workloop here
        </button>
        <button
          type="button"
          onClick={onCloseOther}
          className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
        >
          Close other tab
        </button>
      </div>
    </div>
  </div>
);

const TabSessionSupersededOverlay = ({ onReclaim, onCloseTab }) => (
  <div className="fixed inset-0 z-[1999] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm px-4">
    <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        Session moved to another tab
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        Workloop is now active in another browser tab. This tab is paused.
      </p>
      <div className="mt-6 flex flex-col gap-2.5">
        <button
          type="button"
          onClick={onReclaim}
          className="w-full px-4 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          Use Workloop here
        </button>
        <button
          type="button"
          onClick={onCloseTab}
          className="w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
        >
          Close this tab
        </button>
      </div>
    </div>
  </div>
);

const TabSessionManager = ({ isAuthenticated, token }) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const isCallTab = isDesktopCallWindow()
    || location.pathname === '/call'
    || location.pathname.startsWith('/call/');
  const isAttendanceAnalyticsTab = location.pathname === '/attendance/analytics'
    || location.pathname.startsWith('/attendance/analytics/');
  const tabSessionEnabled = isAuthenticated
    && location.pathname !== SESSION_CLOSED_PATH
    && !isCallTab
    && !isAttendanceAnalyticsTab
    && !isElectronRuntime();
  const sessionUserKey = user?.username || getTabSessionUserKey();

  const {
    isSuperseded,
    showSessionConflict,
    useWorkloopHere,
    closeOtherSession,
    reclaimSession,
    leaveToSessionClosedPage,
  } = useTabSession(tabSessionEnabled, sessionUserKey, token);

  if (!tabSessionEnabled) return null;

  return (
    <>
      {showSessionConflict && (
        <TabSessionConflictModal
          onUseHere={useWorkloopHere}
          onCloseOther={closeOtherSession}
        />
      )}
      {isSuperseded && !showSessionConflict && (
        <TabSessionSupersededOverlay
          onReclaim={reclaimSession}
          onCloseTab={leaveToSessionClosedPage}
        />
      )}
    </>
  );
};

export default TabSessionManager;
